<template>
  <div ref="container" :class="['pf-legend__container', containerClass]">
    <div
      :class="['pf-legend__item', configClass]"
      v-for="d in adjustData"
      :key="d.id"
      :ref="d.id"
      :style="`top:${d.y - topPos}px;`"
      @mouseenter="onFocus(d.id)"
      @focus="onFocus(d.id)"
      @mouseleave="onFocus(null)"
      @blur="onFocus(null)"
      @click="configuration ? emitClick(d.id) : null"
    >
      <svg
        v-if="keyShape"
        :class="'pf-legend__item-svg'"
        aria-hidden="true"
        role="presentational"
        :viewbox="itemKeyViewbox"
        :width="itemKeyWidth"
        :height="itemKeyHeight"
      >
        <defs>
          <slot name="pattern" />
        </defs>
        <component
          :class="'pf-legend__item-key'"
          :is="keyShape(d.key)"
          v-bind="keyAttributes(d.key)"
        />
      </svg>
      <span
        :class="'pf-legend__item-label'"
        :contenteditable="configuration"
        @blur="configuration ? updateLabel($event, d) : null"
      >
        {{ d.key.label }}</span
      >
      <span v-if="legendValues" :class="'pf-legend__item-value'" :style="{ color: d.key.color }">
        {{ d.value ? onFormat(d.value.y) : '—' }}
      </span>
    </div>
  </div>
</template>

<script>
  import { scaleLinear, scaleBand, scaleSymlog, scaleTime } from 'd3-scale';
  import formatValue from '@/utils/valueFormatter';

  export default {
    name: 'PfLineLegend',
    props: {
      configuration: {
        type: Boolean,
      },
      data: {
        type: Array,
        required: true,
      },
      dashed: {
        type: [Boolean, Array],
        default: false,
      },
      domainX: {
        type: Array,
        required: true,
      },
      domainY: {
        type: Array,
        required: true,
      },
      height: {
        type: Number,
      },
      legendValues: {
        type: Boolean,
        default: true,
      },
      itemFormat: {
        type: Object,
      },
      legendProps: {
        type: Array,
      },
      plotIndex: {
        type: Number,
      },
      rangeX: {
        type: Array,
        required: true,
      },
      rangeY: {
        type: Array,
        required: true,
      },
      shrink: {
        type: Boolean,
        default: false,
      },
      scaleTypeX: {
        type: String,
        default: 'band',
        validator(value) {
          return ['band', 'time'].indexOf(value) !== -1;
        },
      },
      scaleTypeY: {
        type: String,
        default: 'linear',
        validator(value) {
          return ['linear', 'symlog'].indexOf(value) !== -1;
        },
      },
      symlogConstant: {
        type: Number,
        default: 1,
      },
      width: {
        type: Number,
      },
      showOutline: {
        type: Array,
      },
      lineWeight: {
        type: Array,
      },
    },
    data() {
      return {
        itemKeyViewbox: '0 0 14 14',
        itemKeyWidth: '100%',
        itemKeyHeight: 14,
        lineHeight: 21,
        topPos: 10,
        maxChars: 14,
        topY: 4,
        scales: {
          band: scaleBand,
          linear: scaleLinear,
          symlog: scaleSymlog,
          time: scaleTime,
        },
      };
    },
    computed: {
      configClass() {
        const obj = {};
        obj['pf-configuration__select'] = this.configuration;
        return obj;
      },
      containerClass() {
        const obj = {};
        obj['pf-legend__container-configuration'] = this.configuration;
        obj['pf-legend__container-no-border'] = this.noTopBorder;
        return obj;
      },
      adjustData() {
        const filtered = [];

        this.data.forEach((d, i) => {
          const chars = this.legendProps[i].label.length;
          const lines = Math.ceil(chars / this.maxChars);
          const boundHeight = (lines + this.includeValue) * this.lineHeight + this.itemKeyHeight;
          const lowerBound = d[d.length - 1].value
            ? this.yScale(d[d.length - 1].value.y) + boundHeight
            : boundHeight;
          let y = d[d.length - 1].value ? this.yScale(d[d.length - 1].value.y) : null;
          y = !y ? this.topY : y; // if top is null add padding

          filtered.push({
            id: d[d.length - 1].id,
            y,
            chars,
            lines,
            height: boundHeight,
            lowerBound,
            key: {
              ...this.legendProps[i],
              showOutline: this.showOutline[i],
              lineWeight: this.lineWeight[i],
            },
            value: d[d.length - 1].value,
          });
        });

        filtered.sort((a, b) => a.y - b.y);

        filtered.forEach((d, i) => {
          if (i > 0) {
            const lowerBound =
              filtered[0].y === this.topPos
                ? filtered[i - 1].lowerBound + this.topPos
                : filtered[i - 1].lowerBound; // if top needs padding, pad subsequent values
            const adj = d.y < lowerBound;
            d.y = adj ? lowerBound : d.y;
            d.lowerBound = d.y + d.height;
          }
        });

        if (filtered[filtered.length - 1].lowerBound > this.height) {
          filtered.forEach((d, i) => {
            d.y = i > 0 ? filtered[i - 1].lowerBound : this.topY;
            d.lowerBound = d.y + d.height;
          });
        }

        return filtered;
      },
      noTopBorder() {
        return this.adjustData ? this.adjustData[0].y <= this.topY : false;
      },
      lineCoordinates() {
        const filtered = [];
        this.data.forEach((d, i) => {
          d[d.length - 1].y = this.yScale(d[d.length - 1].value.y);
          d[d.length - 1].key = this.legendProps[i];
          filtered.push(d[d.length - 1]);
        });

        return filtered;
      },
      lineWidth() {
        return this.$refs.container ? this.$refs.container.$el.getBoundingClientRect().width : 0;
      },
      includeValue() {
        return this.legendValues ? 1 : 0;
      },
      xOffset() {
        return this.scaleTypeX !== 'time' ? this.xScale.bandwidth() / 2 : 0;
      },
      xScale() {
        const scale = this.scales[this.scaleTypeX]()
          .domain(this.scaleTypeX === 'band' ? this.scaleBandDomain : this.domainX)
          .range(this.rangeX);

        return scale;
      },
      yScale() {
        const scale = this.scales[this.scaleTypeY]()
          .domain(this.domainY)
          .range(this.rangeY)
          .clamp(true);

        if (this.scaleTypeY === 'symlog') {
          scale.constant(this.symlogConstant);
        }

        return scale;
      },
    },
    watch: {
      plotIndex(val) {
        this.updateSelect(val);
      },
    },
    methods: {
      formatValue,
      onFormat(val) {
        return this.formatValue(val, this.itemFormat, true);
      },
      onFocus(data) {
        this.$emit('pffocusdata', data);
      },
      keyShape(key) {
        const shapes = {
          line: ['line-dashed', 'benchmark-bar', 'line-solid'],
        };

        const [shape = null] = Object.keys(shapes).filter((s) => shapes[s].includes(key.keyType));
        return shape;
      },
      keyAttributes(key) {
        return {
          ...this.weightedLineAttributes(key), // TODO
          x1: 0,
          x2: '100%',
          y1: 7,
          y2: 7,
          stroke: key.color,
          'stroke-width': 2,
          'stroke-linecap': 'round',
        };
      },
      calcLineWeight(key) {
        return key.keyType === 'benchmark-bar' || !key.lineWeight ? 2 : key.lineWeight;
      },
      weightedLineAttributes(key) {
        const attr = {
          2: { 'stroke-dasharray': '1 4' },
          4: { 'stroke-dasharray': '1 8' },
          6: { 'stroke-dasharray': '1 6' },
        };

        const selectedAttrs = attr[this.calcLineWeight(key)];

        if (key.keyType !== 'line-dashed') {
          selectedAttrs['stroke-dasharray'] = null;
        }

        return selectedAttrs;
      },
      updateSelect(id) {
        this.adjustData.forEach((item) => {
          if (`${id}`.includes(item.id) || `${id}` === item.id.split('_')[1]) {
            this.$refs[item.id][0].classList.add('pf-selected-elem');
          } else {
            this.$refs[item.id][0].classList.remove('pf-selected-elem');
          }
        });
      },
      emitClick(args) {
        const comp = args.split('_');
        const ndx = comp[1];

        this.updateSelect(args);

        this.$emit('pflineselect', ndx);
      },
      updateLabel(e, d) {
        const comp = d.id.split('_');
        const ndx = comp[1];
        const allowUpdate = true;

        this.$emit('pflegendupdate', { $event: e, ndx, allowUpdate });
      },
    },
  };
</script>

<style lang="scss">
  @import '@mce/visual-language';
  @import '@mds/constants';
  @import '@mds/typography';

  .pf-legend {
    &__container {
      position: relative;
      border-top: $mce-legend-border;
      border-bottom: $mce-legend-border;
      width: 100%;
      height: 100%;
      padding: $mds-space-half-x 0;
      overflow: auto;

      &-no-border {
        border-top: 0;
        padding: 0 0 4px;
      }

      &-configuration {
        display: table;
      }
    }

    &__item {
      position: absolute;
      display: flex;
      flex-direction: column;

      &-label {
        overflow-wrap: anywhere;

        @include mds-body-text-m();
      }

      &-value {
        overflow-wrap: anywhere;
        font-weight: 600;
      }
    }
  }

  .pf-configuration__select {
    .pf-legend__item-label:hover {
      background: rgba($mds-interactive-color-primary-default, 0.25);
      border-radius: 5px;
    }
  }
</style>
